/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Play, Pause, Volume2, VolumeX, Music } from 'lucide-react';

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) { return '0:00'; }
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const AudioPlayer = ({ src, fileName }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) { return; }
    if (a.paused) { a.play().catch(() => {}); } else { a.pause(); }
  };

  const handleSeek = (e) => {
    const a = audioRef.current;
    if (!a) { return; }
    a.currentTime = Number(e.target.value);
  };

  const handleVolume = (e) => {
    const value = Number(e.target.value);
    setVolume(value);
    if (audioRef.current) {
      audioRef.current.volume = value;
      audioRef.current.muted = value === 0;
    }
    setMuted(value === 0);
  };

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) { return; }
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  return (
    <div className="bg-base-200 p-6">
      <audio
        ref={audioRef}
        src={src}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
      />

      <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 rounded-2xl bg-primary/10">
        <Music size={32} className="text-primary" />
      </div>
      <p className="text-sm font-bold text-base-content text-center truncate mb-6" title={fileName}>{fileName}</p>

      <div className="flex items-center gap-3">
        <button
          onClick={togglePlay}
          aria-label={playing ? 'Pause' : 'Play'}
          className="flex-shrink-0 p-2 rounded-full bg-primary text-primary-content hover:opacity-90 transition"
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
        <span className="text-xs font-bold text-base-content/60 w-10 text-right flex-shrink-0">{formatTime(currentTime)}</span>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          aria-label="Seek"
          className="range range-primary range-xs flex-1"
        />
        <span className="text-xs font-bold text-base-content/60 w-10 flex-shrink-0">{formatTime(duration)}</span>
        <button onClick={toggleMute} aria-label={muted || volume === 0 ? 'Unmute' : 'Mute'} className="flex-shrink-0 p-1.5 text-base-content/60 hover:text-primary transition">
          {muted || volume === 0 ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={muted ? 0 : volume}
          onChange={handleVolume}
          aria-label="Volume"
          className="range range-primary range-xs w-20 flex-shrink-0"
        />
      </div>
    </div>
  );
};

export default AudioPlayer;

AudioPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  fileName: PropTypes.string,
};
